import type {
  DiscoveryDetail,
  DiscoveryEpisode,
  DiscoveryHubsResponse,
  DiscoveryItem,
  DiscoveryMediaType,
  DiscoveryPerson,
  DiscoverySeasonResponse,
  DiscoverySeasonSummary,
  DiscoverySearchResponse,
} from "@rago/web-shared";
import { api } from "../lib/apiClient";
import type {
  Hub,
  ImageVariant,
  MediaItem,
  PlaybackInfo,
  RagoSource,
  UserItemState,
} from "./types";

/**
 * Rago Discovery source — reads the server-side TMDB proxy (`/v1/discover/*`)
 * and maps results onto MediaItem/Hub so it reuses MediaCard/HubRow. Images
 * are loaded straight from TMDB (the API returns paths only). It's a browse
 * source: playback methods are inert, and it is NOT part of getSources() (the
 * media-server registry). MediaCard deep-links its items to the Discovery
 * detail route via `kind === "discovery"`.
 */

const TMDB_IMG = "https://image.tmdb.org/t/p";
const POSTER_SIZES: Array<[number, string]> = [
  [342, "w342"],
  [500, "w500"],
  [780, "w780"],
];
const BACKDROP_SIZES: Array<[number, string]> = [
  [780, "w780"],
  [1280, "w1280"],
  [3840, "original"],
];
/** TMDB cuts episode stills at w92/w185/w300/original only — the 576px
 * card/hero thumb request lands on `original`, the same URL the episode
 * sheet loads, so opening it is a cache hit. */
const STILL_SIZES: Array<[number, string]> = [
  [300, "w300"],
  [1920, "original"],
];

/** Build a TMDB image URL for a raw path, or null. */
export function tmdbImageUrl(path: string | null | undefined, size: string): string | null {
  return path ? `${TMDB_IMG}/${size}${path}` : null;
}

function variants(path: string | null, sizes: Array<[number, string]>): ImageVariant[] {
  if (!path) return [];
  return sizes.map(([width, size]) => ({ width, url: `${TMDB_IMG}/${size}${path}` }));
}

const ZERO_STATE: UserItemState = {
  watched: false,
  viewOffsetMs: 0,
  viewCount: 0,
  lastPlayedAt: null,
  audioStreamIndex: null,
  subtitleStreamIndex: null,
};

export function toMediaItem(d: DiscoveryItem): MediaItem {
  return {
    id: d.id,
    sourceId: "discovery",
    guid: `tmdb://${d.mediaType}/${d.id}`,
    // TMDB says "tv"; the seam speaks the server's vocabulary: "show".
    type: d.mediaType === "tv" ? "show" : "movie",
    title: d.title,
    sortTitle: d.title.toLowerCase(),
    year: d.year ?? 0,
    summary: d.overview,
    genres: d.genres,
    // Only where the API looked the title up: detail, and the hubs' lead item.
    runtimeMs: d.runtimeMs ?? 0,
    externalIds: [{ provider: "tmdb", id: d.id }],
    addedAt: "",
    images: {
      poster: variants(d.posterPath, POSTER_SIZES),
      backdrop: variants(d.backdropPath, BACKDROP_SIZES),
    },
    seasonCount: d.seasonCount,
  };
}

/**
 * A TMDB season as a MediaItem, so the Discovery show page rides the same
 * SeasonsGrid/MediaCard as owned shows. parentId + index carry what the
 * card's deep-link needs (/discover/tv/{parentId}/season/{index}), and
 * childCount/year feed captionFor ("13 episodes") and the hero's meta row.
 */
export function toSeasonMediaItem(s: DiscoverySeasonSummary, tvId: string): MediaItem {
  return {
    id: `${tvId}-s${s.seasonNumber}`,
    sourceId: "discovery",
    guid: `tmdb://tv/${tvId}/season/${s.seasonNumber}`,
    type: "season",
    title: s.name,
    sortTitle: s.name.toLowerCase(),
    year: s.airYear ?? 0,
    summary: s.overview,
    genres: [],
    runtimeMs: 0,
    externalIds: [],
    addedAt: "",
    images: { poster: variants(s.posterPath, POSTER_SIZES), backdrop: [] },
    index: s.seasonNumber,
    childCount: s.episodeCount,
    parentId: tvId,
  };
}

/**
 * A TMDB episode as a MediaItem for the season page's EpisodesGrid. The
 * episode block mirrors the server's hierarchy fields, so captionFor and
 * metaFacts read exactly as they do for owned episodes ("Show · S2 · E3",
 * "Season 2 · Episode 3 · date · runtime").
 */
export function toEpisodeMediaItem(
  ep: DiscoveryEpisode,
  ctx: { tvId: string; seasonNumber: number; showTitle: string },
): MediaItem {
  const year = Number.parseInt((ep.airDate ?? "").slice(0, 4), 10);
  return {
    id: `${ctx.tvId}-s${ctx.seasonNumber}e${ep.episodeNumber}`,
    sourceId: "discovery",
    guid: `tmdb://tv/${ctx.tvId}/season/${ctx.seasonNumber}/episode/${ep.episodeNumber}`,
    type: "episode",
    title: ep.name,
    sortTitle: ep.name.toLowerCase(),
    year: Number.isFinite(year) ? year : 0,
    summary: ep.overview,
    genres: [],
    runtimeMs: ep.runtimeMs,
    externalIds: [],
    addedAt: "",
    images: { poster: [], backdrop: [], thumb: variants(ep.stillPath, STILL_SIZES) },
    index: ep.episodeNumber,
    airDate: ep.airDate,
    episode: {
      index: ep.episodeNumber,
      parentId: `${ctx.tvId}-s${ctx.seasonNumber}`,
      parentIndex: ctx.seasonNumber,
      grandparentId: ctx.tvId,
      grandparentTitle: ctx.showTitle,
    },
  };
}

class DiscoverySource implements RagoSource {
  readonly id = "discovery";
  readonly kind = "discovery" as const;
  readonly name = "Rago Discovery";

  async getLibraries() {
    return [];
  }
  async getLibraryItems() {
    return { items: [], total: 0 };
  }
  async getItem() {
    return null;
  }

  async getHomeHubs(): Promise<Hub[]> {
    const res = await api<DiscoveryHubsResponse>("/v1/discover/hubs");
    return res.rows.map((row) => ({
      id: row.id,
      title: row.title,
      items: row.items.map(toMediaItem),
    }));
  }

  async search(query: string): Promise<MediaItem[]> {
    const q = query.trim();
    if (!q) return [];
    const res = await api<DiscoverySearchResponse>(
      `/v1/discover/search?q=${encodeURIComponent(q)}`,
    );
    return res.items.map(toMediaItem);
  }

  /** Full TMDB detail — extra method beyond the RagoSource seam. */
  async getDetail(mediaType: DiscoveryMediaType, id: string): Promise<DiscoveryDetail> {
    return api<DiscoveryDetail>(`/v1/discover/item/${mediaType}/${id}`);
  }

  async getPerson(id: string): Promise<DiscoveryPerson> {
    return api<DiscoveryPerson>(`/v1/discover/person/${id}`);
  }

  async getSeason(tvId: string, seasonNumber: number): Promise<DiscoverySeasonResponse> {
    return api<DiscoverySeasonResponse>(`/v1/discover/season/${tvId}/${seasonNumber}`);
  }

  imageUrl(item: MediaItem, kind: "poster" | "backdrop" | "thumb", minWidth: number): string {
    const vs = item.images[kind] ?? [];
    const pick = vs.find((v) => v.width >= minWidth) ?? vs.at(-1);
    return pick?.url ?? "";
  }

  async getPlayback(): Promise<PlaybackInfo> {
    throw new Error("Rago Discovery items are not playable");
  }
  getUserState(): UserItemState {
    return ZERO_STATE;
  }
  reportProgress() {}
  setWatched() {}
}

export const discoverySource = new DiscoverySource();
